/**
 * Cmarkdownxychart — Mermaid xychart parser
 *
 * Accepts the Mermaid `xychart-beta` grammar (and a permissive `xychart`)
 * with an optional `horizontal` flag. Recognized lines:
 *   xychart-beta [horizontal]
 *   title "..."
 *   x-axis "Title" [a, b, c]                  // categorical
 *   x-axis "Title" 0 --> 100                  // numeric (length matches series)
 *   y-axis "Title" 0 --> 100                  // numeric range
 *   y-axis "Title"                            // title only
 *   bar  [v1, v2, ...]
 *   line [v1, v2, ...]
 *
 * Lines beginning with `%%` are treated as comments and stripped.
 */

import {
  XYCHART_AXIS_TYPE,
  XYCHART_SERIES_TYPE,
  type XYChartDataSeries,
  type XYChartParsed,
  type XYChartSeriesType,
  type XYChartXAxis,
} from './xychart-types';

const NUMBER_PATTERN = String.raw`[+-]?(?:\d*\.\d+|\d+\.?\d*)`;

const XYCHART_HEADER_REGEX = /^xychart(?:-beta)?(?:\s+horizontal)?\s*$/i;
const TITLE_QUOTED_REGEX = /^title\s+"([^"]+)"\s*$/i;
const TITLE_UNQUOTED_REGEX = /^title\s+(\S+(?:\s+\S+)*)\s*$/i;
const X_AXIS_CATEGORICAL_REGEX = /^x-axis\s+(?:"([^"]+)"\s+)?\[([^\]]+)\]\s*$/i;
const X_AXIS_NUMERIC_REGEX = new RegExp(
  `^x-axis\\s+(?:\\"([^\\"]+)\\"\\s+)?(${NUMBER_PATTERN})\\s*-->\\s*(${NUMBER_PATTERN})\\s*$`,
  'i'
);
const Y_AXIS_RANGE_REGEX = new RegExp(
  `^y-axis\\s+(?:\\"([^\\"]+)\\"\\s+)?(${NUMBER_PATTERN})\\s*-->\\s*(${NUMBER_PATTERN})\\s*$`,
  'i'
);
const Y_AXIS_TITLE_ONLY_REGEX = /^y-axis\s+"([^"]+)"\s*$/i;
const SERIES_REGEX = /^(bar|line)\s+(?:"([^"]+)"\s+)?\[([^\]]+)\]\s*$/i;
const CHART_DIRECTIVE_PREFIX_REGEX = /^(?:x-axis|y-axis|bar|line)/i;
const WRAPPING_QUOTE_PAIRS: ReadonlyArray<Readonly<{ open: string; close: string }>> = [
  { open: '"', close: '"' },
  { open: "'", close: "'" },
];

const SERIES_TYPE_BY_TOKEN: Record<string, XYChartSeriesType> = {
  [XYCHART_SERIES_TYPE.Bar]: XYCHART_SERIES_TYPE.Bar,
  [XYCHART_SERIES_TYPE.Line]: XYCHART_SERIES_TYPE.Line,
};

enum XYChartLineHandlerKey {
  Title = 'title',
  XAxisCategorical = 'xAxisCategorical',
  XAxisNumeric = 'xAxisNumeric',
  YAxisRange = 'yAxisRange',
  YAxisTitle = 'yAxisTitle',
  Series = 'series',
}

interface XYChartParseState {
  parsed: XYChartParsed;
  barCount: number;
  lineCount: number;
}

interface XYChartLineHandlerInput {
  state: XYChartParseState;
  line: string;
}

type XYChartLineHandler = (input: XYChartLineHandlerInput) => XYChartParseState | null;

interface XYChartNumericRange {
  min: number;
  max: number;
}

interface XYChartParsedSeriesLine {
  type: XYChartSeriesType;
  data: number[];
  explicitLabel: string | undefined;
}

interface XYChartSeriesCounts {
  barCount: number;
  lineCount: number;
}

const SERIES_COUNT_DELTAS: Record<XYChartSeriesType, XYChartSeriesCounts> = {
  [XYCHART_SERIES_TYPE.Bar]: { barCount: 1, lineCount: 0 },
  [XYCHART_SERIES_TYPE.Line]: { barCount: 0, lineCount: 1 },
};

function sanitizeMermaidLine(line: string): string {
  const commentStart = line.indexOf('%%');
  const withoutComment = commentStart >= 0 ? line.slice(0, commentStart) : line;
  return withoutComment.trim();
}

function parseNumeric(value: string): number | null {
  const parsed = Number.parseFloat(value.trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function stripWrappingQuotes(value: string): string {
  const trimmed = value.trim();
  const hasWrappingQuotes = WRAPPING_QUOTE_PAIRS.some(
    (quotePair) => trimmed.startsWith(quotePair.open) && trimmed.endsWith(quotePair.close)
  );
  if (hasWrappingQuotes) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
}

function parseCategories(raw: string): string[] {
  return raw
    .split(',')
    .map((category) => stripWrappingQuotes(category))
    .filter((category) => category.length > 0);
}

function parseSeriesValues(raw: string): number[] {
  return raw
    .split(',')
    .map((chunk) => parseNumeric(stripWrappingQuotes(chunk)))
    .filter((value): value is number => value != null);
}

const REGEX_FIRST_CAPTURE_REF = '$1';

function trimTrailingZeros(value: string): string {
  return value.replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, REGEX_FIRST_CAPTURE_REF);
}

function formatPlainNumber(value: number, maxFractionDigits = 2): string {
  if (Number.isInteger(value)) {
    return String(value);
  }
  return trimTrailingZeros(value.toFixed(maxFractionDigits));
}

function createAutoCategories(count: number): string[] {
  return Array.from({ length: count }, (_, index) => String(index + 1));
}

function createNumericCategories(input: { min: number; max: number; count: number }): string[] {
  if (input.count <= 0) return [];
  if (input.count === 1 || input.min === input.max) {
    return Array.from({ length: input.count }, () => formatPlainNumber(input.min));
  }
  const step = (input.max - input.min) / (input.count - 1);
  return Array.from({ length: input.count }, (_, index) =>
    formatPlainNumber(input.min + step * index)
  );
}

function normalizeSeriesLength(input: { data: number[]; targetLength: number }): number[] {
  if (input.targetLength <= 0) return [];
  if (input.data.length === input.targetLength) return input.data;
  if (input.data.length > input.targetLength) {
    return input.data.slice(0, input.targetLength);
  }
  return [
    ...input.data,
    ...Array.from({ length: input.targetLength - input.data.length }, () => 0),
  ];
}

function defaultSeriesLabel(input: {
  type: typeof XYCHART_SERIES_TYPE.Bar | typeof XYCHART_SERIES_TYPE.Line;
  barCount: number;
  lineCount: number;
}): string {
  if (input.type === XYCHART_SERIES_TYPE.Bar) return `Bar ${input.barCount}`;
  return `Line ${input.lineCount}`;
}

function parseSeriesType(input: { token: string }): XYChartSeriesType | null {
  return SERIES_TYPE_BY_TOKEN[input.token.toLowerCase()] ?? null;
}

function parseNumericRange(input: {
  minToken: string;
  maxToken: string;
}): XYChartNumericRange | null {
  const min = parseNumeric(input.minToken);
  const max = parseNumeric(input.maxToken);
  if (min == null || max == null) {
    return null;
  }

  return { min, max };
}

function applyTitleLine(input: XYChartLineHandlerInput): XYChartParseState | null {
  const titleQuoted = input.line.match(TITLE_QUOTED_REGEX);
  if (titleQuoted) {
    return {
      ...input.state,
      parsed: { ...input.state.parsed, title: titleQuoted[1].trim() },
    };
  }

  const titleUnquoted = input.line.match(TITLE_UNQUOTED_REGEX);
  if (!titleUnquoted || CHART_DIRECTIVE_PREFIX_REGEX.test(input.line)) {
    return null;
  }

  return {
    ...input.state,
    parsed: { ...input.state.parsed, title: titleUnquoted[1].trim() },
  };
}

function applyCategoricalXAxisLine(input: XYChartLineHandlerInput): XYChartParseState | null {
  const xCategorical = input.line.match(X_AXIS_CATEGORICAL_REGEX);
  if (!xCategorical) {
    return null;
  }

  return {
    ...input.state,
    parsed: {
      ...input.state.parsed,
      xAxis: {
        title: xCategorical[1]?.trim() ?? input.state.parsed.xAxis.title,
        type: XYCHART_AXIS_TYPE.Categorical,
        categories: parseCategories(xCategorical[2]),
        min: null,
        max: null,
      },
    },
  };
}

function applyNumericXAxisLine(input: XYChartLineHandlerInput): XYChartParseState | null {
  const xNumeric = input.line.match(X_AXIS_NUMERIC_REGEX);
  if (!xNumeric) {
    return null;
  }

  const range = parseNumericRange({ minToken: xNumeric[2], maxToken: xNumeric[3] });
  if (!range) {
    return null;
  }

  return {
    ...input.state,
    parsed: {
      ...input.state.parsed,
      xAxis: {
        title: xNumeric[1]?.trim() ?? input.state.parsed.xAxis.title,
        type: XYCHART_AXIS_TYPE.Numeric,
        categories: [],
        min: range.min,
        max: range.max,
      },
    },
  };
}

function applyYAxisRangeLine(input: XYChartLineHandlerInput): XYChartParseState | null {
  const yRange = input.line.match(Y_AXIS_RANGE_REGEX);
  if (!yRange) {
    return null;
  }

  const range = parseNumericRange({ minToken: yRange[2], maxToken: yRange[3] });
  if (!range) {
    return null;
  }

  return {
    ...input.state,
    parsed: {
      ...input.state.parsed,
      yAxis: {
        title: yRange[1]?.trim() ?? input.state.parsed.yAxis.title,
        min: range.min,
        max: range.max,
      },
    },
  };
}

function applyYAxisTitleLine(input: XYChartLineHandlerInput): XYChartParseState | null {
  const yTitleOnly = input.line.match(Y_AXIS_TITLE_ONLY_REGEX);
  if (!yTitleOnly) {
    return null;
  }

  return {
    ...input.state,
    parsed: {
      ...input.state.parsed,
      yAxis: { title: yTitleOnly[1].trim(), min: null, max: null },
    },
  };
}

function parseMatchedSeriesLine(input: {
  seriesMatch: RegExpMatchArray;
}): XYChartParsedSeriesLine | null {
  const seriesType = parseSeriesType({ token: input.seriesMatch[1] });
  if (seriesType == null) {
    return null;
  }

  const values = parseSeriesValues(input.seriesMatch[3]);
  if (values.length === 0) {
    return null;
  }

  return {
    type: seriesType,
    data: values,
    explicitLabel: input.seriesMatch[2]?.trim(),
  };
}

function parseSeriesLine(input: { line: string }): XYChartParsedSeriesLine | null {
  const seriesMatch = input.line.match(SERIES_REGEX);
  if (!seriesMatch) {
    return null;
  }

  return parseMatchedSeriesLine({ seriesMatch });
}

function updateSeriesCounts(input: {
  counts: XYChartSeriesCounts;
  seriesType: XYChartSeriesType;
}): XYChartSeriesCounts {
  const delta = SERIES_COUNT_DELTAS[input.seriesType];
  return {
    barCount: input.counts.barCount + delta.barCount,
    lineCount: input.counts.lineCount + delta.lineCount,
  };
}

function resolveSeriesLabel(input: {
  parsedSeries: XYChartParsedSeriesLine;
  counts: XYChartSeriesCounts;
}): string {
  if (input.parsedSeries.explicitLabel && input.parsedSeries.explicitLabel.length > 0) {
    return input.parsedSeries.explicitLabel;
  }

  return defaultSeriesLabel({
    type: input.parsedSeries.type,
    barCount: input.counts.barCount,
    lineCount: input.counts.lineCount,
  });
}

function applySeriesLine(input: XYChartLineHandlerInput): XYChartParseState | null {
  const parsedSeries = parseSeriesLine({ line: input.line });
  if (!parsedSeries) {
    return null;
  }

  const counts = updateSeriesCounts({
    counts: { barCount: input.state.barCount, lineCount: input.state.lineCount },
    seriesType: parsedSeries.type,
  });
  const series: XYChartDataSeries = {
    type: parsedSeries.type,
    data: parsedSeries.data,
    colorIndex: input.state.parsed.series.length,
    label: resolveSeriesLabel({ parsedSeries, counts }),
  };

  return {
    parsed: { ...input.state.parsed, series: [...input.state.parsed.series, series] },
    ...counts,
  };
}

const CHART_LINE_HANDLERS: Record<XYChartLineHandlerKey, XYChartLineHandler> = {
  [XYChartLineHandlerKey.Title]: applyTitleLine,
  [XYChartLineHandlerKey.XAxisCategorical]: applyCategoricalXAxisLine,
  [XYChartLineHandlerKey.XAxisNumeric]: applyNumericXAxisLine,
  [XYChartLineHandlerKey.YAxisRange]: applyYAxisRangeLine,
  [XYChartLineHandlerKey.YAxisTitle]: applyYAxisTitleLine,
  [XYChartLineHandlerKey.Series]: applySeriesLine,
};

const CHART_LINE_HANDLER_ORDER: readonly XYChartLineHandlerKey[] = [
  XYChartLineHandlerKey.Title,
  XYChartLineHandlerKey.XAxisCategorical,
  XYChartLineHandlerKey.XAxisNumeric,
  XYChartLineHandlerKey.YAxisRange,
  XYChartLineHandlerKey.YAxisTitle,
  XYChartLineHandlerKey.Series,
];

function applyChartLine(input: XYChartLineHandlerInput): XYChartParseState {
  return (
    CHART_LINE_HANDLER_ORDER.reduce<XYChartParseState | null>(
      (resolvedState, handlerKey) =>
        resolvedState ?? CHART_LINE_HANDLERS[handlerKey](input),
      null
    ) ?? input.state
  );
}

function resolveNumericXAxis(input: { xAxis: XYChartXAxis; categoryCount: number }): XYChartXAxis {
  if (input.xAxis.type !== XYCHART_AXIS_TYPE.Numeric) {
    return input.xAxis;
  }

  const { min, max } = input.xAxis;
  if (min != null && max != null) {
    return {
      ...input.xAxis,
      categories: createNumericCategories({
        min,
        max,
        count: input.categoryCount,
      }),
    };
  }

  return {
    ...input.xAxis,
    type: XYCHART_AXIS_TYPE.Categorical,
    categories: createAutoCategories(input.categoryCount),
  };
}

function resolveXAxis(input: { xAxis: XYChartXAxis; categoryCount: number }): XYChartXAxis {
  const copiedXAxis: XYChartXAxis = {
    ...input.xAxis,
    categories: [...input.xAxis.categories],
  };
  const numericResolvedXAxis = resolveNumericXAxis({
    xAxis: copiedXAxis,
    categoryCount: input.categoryCount,
  });

  if (numericResolvedXAxis.categories.length > 0) {
    return numericResolvedXAxis;
  }

  return {
    ...numericResolvedXAxis,
    type: XYCHART_AXIS_TYPE.Categorical,
    categories: createAutoCategories(input.categoryCount),
  };
}

export function parseMermaidXYChart(raw: string): XYChartParsed | null {
  const lines = raw
    .split('\n')
    .map((line) => sanitizeMermaidLine(line))
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    return null;
  }

  const headerLine = lines[0];
  if (!XYCHART_HEADER_REGEX.test(headerLine)) {
    return null;
  }

  const finalState = lines.slice(1).reduce<XYChartParseState>(
    (state, line) => applyChartLine({ state, line }),
    {
      parsed: {
        title: null,
        horizontal: /\bhorizontal\b/i.test(headerLine),
        xAxis: {
          title: null,
          type: XYCHART_AXIS_TYPE.Categorical,
          categories: [],
          min: null,
          max: null,
        },
        yAxis: { title: null, min: null, max: null },
        series: [],
      },
      barCount: 0,
      lineCount: 0,
    }
  );
  const { parsed } = finalState;

  if (parsed.series.length === 0) {
    return null;
  }

  const firstSeriesLength = parsed.series[0].data.length;
  const resolvedXAxis = resolveXAxis({
    xAxis: parsed.xAxis,
    categoryCount: firstSeriesLength,
  });
  const categoryCount = resolvedXAxis.categories.length;
  const normalizedSeries = parsed.series.map((series) => ({
    ...series,
    data: normalizeSeriesLength({ data: series.data, targetLength: categoryCount }),
  }));

  return {
    ...parsed,
    xAxis: resolvedXAxis,
    series: normalizedSeries,
  };
}
